/**
 * SRES · Personal stage-actuals tracker (Iter82 · Feb 2026).
 *
 * Records how long the user actually spends on each of the 9 wizard
 * stages, per project, in localStorage. The wizard sidebar reads
 * these values and shows a "You: 2 min ✓" chip beside completed
 * stages so the ETA hint becomes a personal-progress signal.
 *
 * Storage shape:
 *   localStorage["sres.stage-actuals:<pid>"] = {
 *     [stageKey]: {
 *       seconds:    <cumulative time spent on the stage, integer sec>,
 *       visits:     <number of times the user opened this stage>,
 *       last_open:  <ISO timestamp>,
 *       completed_at:  <ISO timestamp | null>,
 *     }
 *   }
 *
 * Rules:
 *   • We start a timer when the user lands on a stage; we stop when
 *     they leave that stage or close the tab. We accumulate seconds.
 *   • Any single "visit" longer than 30 minutes is discarded (idle).
 *   • `completed_at` freezes the "You: X min ✓" chip; further visits
 *     still accumulate but don't remove the tick.
 *   • This lives 100 % client-side — no backend changes.
 */
const IDLE_CAP_SECS = 30 * 60;

function _storageKey(pid) {
  return `sres.stage-actuals:${pid}`;
}

function _readAll(pid) {
  if (!pid) return {};
  try {
    const raw = localStorage.getItem(_storageKey(pid));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _writeAll(pid, all) {
  if (!pid) return;
  try {
    localStorage.setItem(_storageKey(pid), JSON.stringify(all));
  } catch {
    /* quota exceeded — silently drop */
  }
}

/** Return `{seconds, visits, completed_at}` for a stage. */
export function getStageActual(pid, stageKey) {
  const all = _readAll(pid);
  return all[stageKey] || { seconds: 0, visits: 0, completed_at: null };
}

/** All accumulated actuals for a project (used by the sidebar). */
export function getAllActuals(pid) {
  return _readAll(pid);
}

/**
 * Register that the user just opened a stage. Returns a `stop`
 * function the caller MUST invoke when the user leaves the stage
 * (URL change or unmount). If not called, we still capture time up
 * to a fresh open of any other stage on the next mount.
 */
export function startStageVisit(pid, stageKey) {
  if (!pid || !stageKey) return () => {};
  const t0 = Date.now();
  const all = _readAll(pid);
  const entry = all[stageKey] || { seconds: 0, visits: 0, completed_at: null };
  entry.visits = (entry.visits || 0) + 1;
  entry.last_open = new Date().toISOString();
  all[stageKey] = entry;
  _writeAll(pid, all);
  let stopped = false;

  const stop = ({ complete = false } = {}) => {
    if (stopped) return;
    stopped = true;
    const dt = Math.round((Date.now() - t0) / 1000);
    if (dt <= 0) return;
    if (dt > IDLE_CAP_SECS) return;  // idle — discard
    const all2 = _readAll(pid);
    const e2 = all2[stageKey] || { seconds: 0, visits: 0 };
    e2.seconds = (e2.seconds || 0) + dt;
    if (complete && !e2.completed_at) {
      e2.completed_at = new Date().toISOString();
    }
    all2[stageKey] = e2;
    _writeAll(pid, all2);
  };

  return stop;
}

/**
 * Mark a stage as completed (idempotent). Called by external code
 * when the stage's own completeness check flips to true.
 */
export function markStageComplete(pid, stageKey) {
  if (!pid || !stageKey) return;
  const all = _readAll(pid);
  const e = all[stageKey] || { seconds: 0, visits: 0 };
  if (!e.completed_at) e.completed_at = new Date().toISOString();
  all[stageKey] = e;
  _writeAll(pid, all);
}

/** Format seconds → "2 min", "45 s", "1 h 3 min". */
export function formatSeconds(sec) {
  if (!sec || sec < 1) return "";
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rem = min - h * 60;
  return `${h} h ${rem} min`;
}
